import type { ReactNode } from "react";

import { Icon } from "./Icon";
import type { IconName } from "./icons/paths";
import { Overlay } from "./Overlay";

/**
 * Модальное подтверждение. Figma: `Overlay · Emergency Stop` (28:68) — 350
 * в ширину, скруглено на 24, паддинг 20, шаг 14, рамка в тон действия.
 *
 * Тональность разделена: разрушающее действие получает рамку и подложку
 * иконки из семейства loss, обычное — нейтральную рамку. В макете нарисован
 * только разрушающий случай, нейтральный собран из тех же токенов.
 *
 * Движение (FE-13): модалка появляется приближением, а не подстановкой, и
 * уходит быстрее, чем приходит, — согласие с решением должно ощущаться
 * мгновенным. Иконка входит следом за карточкой: она здесь главный носитель
 * тональности.
 */
interface ModalProps {
  tone?: "neutral" | "danger";
  icon: IconName;
  title: string;
  body: string;
  onClose: () => void;
  children?: ReactNode;
  footer: ReactNode;
}

export function Modal({ tone = "neutral", icon, title, body, onClose, children, footer }: ModalProps) {
  const isDanger = tone === "danger";

  return (
    <Overlay onClose={onClose} strong labelledBy="modal-title">
      <div
        className={[
          "m-auto flex w-[350px] flex-col gap-[14px] rounded-3xl border border-solid bg-background-elevated p-5",
          "animate-scale-in [[data-state=closing]_&]:animate-scale-out",
          isDanger ? "border-border-loss" : "border-border-subtle",
        ].join(" ")}
      >
        <span
          className={[
            "animate-pop-in flex size-[52px] items-center justify-center rounded-2xl",
            isDanger ? "bg-surface-loss" : "bg-background-raised",
          ].join(" ")}
          style={{ animationDelay: "var(--motion-fast)" }}
        >
          <Icon name={icon} size={26} className={isDanger ? "text-status-loss" : "text-text-primary"} />
        </span>

        <h2 id="modal-title" className="text-h2 font-bold text-text-primary">
          {title}
        </h2>
        <p className="w-[310px] text-caption font-normal text-text-secondary">{body}</p>

        {children}
        {footer}
      </div>
    </Overlay>
  );
}
